import { defineComponent as _defineComponent } from 'vue'
import { buryPoint } from '@/apis/common/buryPoint'
import { translate } from '@/util/commonFun'
import { genUniId } from '@/util/commonFuncs'
import { computed, onMounted, ref, watch } from 'vue'

type Compare = 'eq' | 'gt' | 'lt' | ''
// side
type holeSlotType =
  | 'lightSlot'
  | 'CCPXHole'
  | 'ALS'
  | 'straightener_slots_and_hinged_cup_holes'
  | 'normalHole'
  | 'normalSlot'
  | ''
  | 'LockHole'
  | 'normal_hole'

// 常规孔规则
interface NormalHoleRule {
  compare: Compare
  value: string
}
// 常规槽规则
interface NormalSlotRule {
  long_side_compare: Compare
  long_side_value: string
  short_side_compare: Compare
  short_side_value: string
}
interface DataSource {
  key: string
  idx: number
  special: holeSlotType
  type: 'hole' | 'slot' | 'special' | ''
  side: 'front' | 'back'
  webId: string
  rules: NormalHoleRule | NormalSlotRule | undefined
}
export interface TypeSide {
  key: holeSlotType
  value: 'front' | 'back'
  type: 'hole' | 'slot' | 'special' | ''
  rules: NormalHoleRule | NormalSlotRule | undefined
  webId?: string
}

// 后端接口的返回规则/负载
interface ResRules {
  is_open: boolean
  special_hole_slot_side: TypeSide[]
}

export default /*#__PURE__*/_defineComponent({
  __name: 'specialHoleAndSlotTable',
  props: {
    special_hole_slot_side_rules: { default: () => [] },
    is_open: { type: Boolean, default: false },
    keyType: { default: '' }
  },
  emits: ['deleteStrHine'],
  setup(__props: any, { expose, emit }) {

const props = __props as { special_hole_slot_side_rules: TypeSide[], is_open: boolean, keyType: string }


// end
const commonsDefult = [
  {
    title: translate('common.inde'),
    dataIndex: 'idx',
    rowDrag: true,
    ellipsis: true,
  },
  {
    title: translate(
      props?.keyType === 'setThroughHoleDrilling'
        ? 'cuttingDock.cuttingParams.holeType'
        : 'cuttingDock.cuttingParams.specialSlotsHoles'
    ),
    dataIndex: 'special',
    scopedSlots: { customRender: 'special' },
    ellipsis: true,
  },
  {
    title: translate('cuttingDock.cuttingParams.specialRules'),
    dataIndex: 'other',
    scopedSlots: { customRender: 'other' },
    ellipsis: true,
  },
  {
    title: translate('cuttingDock.cuttingParams.FixedmachSurface'),
    dataIndex: 'side',
    scopedSlots: { customRender: 'side' },
    ellipsis: true,
  },
  {
    title: translate('common.operation'),
    dataIndex: 'action',
    scopedSlots: { customRender: 'operation' },
    ellipsis: true,
  },
]
const columns = computed(() => {
  if (props?.keyType === 'setThroughHoleDrilling') {
    // 不生效翻面两次打穿孔槽
    return commonsDefult.filter((item) => item.dataIndex !== 'side')
  } else {
    return commonsDefult
  }
})
const compareOpt = computed(() => [
  {
    value: 'gt',
    label: translate('lineManager.greater'),
  },
  {
    value: 'lt',
    label: translate('lineManager.less'),
  },
  {
    value: 'eq',
    label: translate('lineManager.equal'),
  },
])

function handleBlur(record: DataSource) {
  const idx = record.idx - 1
  const rules = bindValList.value[idx]?.rules as NormalHoleRule | undefined
  if (!rules) return

  const nextValue = normalizePositiveDecimalInput(rules.value)
  rules.value = nextValue
  if (record.rules && 'value' in record.rules) {
    ;(record.rules as NormalHoleRule).value = nextValue
  }
}

const POSITIVE_DECIMAL_REG = /^(\d{0,4})(\.(\d{0,2}))?$/

function correctPositiveDecimalRaw(inputValue: string): string {
  let correctedValue = inputValue.replace(/[^\d.]/g, '')
  const dotParts = correctedValue.split('.')
  if (dotParts.length > 2) {
    correctedValue = dotParts[0] + '.' + dotParts.slice(1).join('')
  }

  const parts = correctedValue.split('.')
  if (parts.length === 2) {
    correctedValue = parts[0].slice(0, 4) + '.' + parts[1].slice(0, 2)
  } else if (parts.length === 1) {
    correctedValue = parts[0].slice(0, 4)
  }

  return correctedValue
}

/**
 * @description 失焦时强制规范化：去掉非法字符、合并小数点、截断位数
 */
function normalizePositiveDecimalInput(inputValue: string): string {
  if (!inputValue) return ''

  let correctedValue = correctPositiveDecimalRaw(String(inputValue))
  if (correctedValue.endsWith('.')) {
    correctedValue = correctedValue.slice(0, -1)
  }

  if (POSITIVE_DECIMAL_REG.test(correctedValue)) return correctedValue

  return ''
}

/**
 * @description 格式化孔直径输入：整数最多4位，小数最多2位，如 2211.21
 */
function formatPositiveDecimalInput(inputValue: string): string | null {
  if (inputValue === '') return ''

  if (POSITIVE_DECIMAL_REG.test(inputValue)) return inputValue

  const correctedValue = correctPositiveDecimalRaw(inputValue)
  if (POSITIVE_DECIMAL_REG.test(correctedValue)) return correctedValue

  return null
}

function handleInput(e: Event, record: DataSource) {
  const target = e.target as HTMLInputElement
  const idx = record.idx - 1
  const rules = bindValList.value[idx]?.rules as NormalHoleRule | undefined
  if (!rules) return

  const prevValue = rules.value
  const nextValue = formatPositiveDecimalInput(target.value)

  if (nextValue !== null) {
    rules.value = nextValue
    return
  }

  rules.value = prevValue
  target.value = prevValue
}

const holeSlotsOpt = computed(() =>
  props?.keyType === 'setThroughHoleDrilling'
    ? ([
        {
          value: 'LockHole',
          label: translate('common.Keyhole'),
        },
        {
          value: 'normal_hole',
          label: translate('common.ConventionalHole'),
        },
      ] as const)
    : ([
        {
          value: 'lightSlot',
          label: translate('common.lightSlot'),
        },
        {
          value: 'CCPXHole',
          label: translate('common.CCPXHole'),
        },
        {
          value: 'ALS',
          label: translate('common.ALS'),
        },
        {
          value: 'straightener_slots_and_hinged_cup_holes',
          label: translate('common.stretchHinge'),
        },
        {
          value: 'normalHole',
          label: translate('cuttingDock.cuttingParams.normalHole'),
        },
        {
          value: 'normalSlot',
          label: translate('cuttingDock.cuttingParams.normalSlot'),
        },
      ] as const)
)
const surfanceOpt = computed(() => [
  {
    value: 'front',
    label: translate('cuttingDock.cuttingParams.blankFront'),
  },
  {
    value: 'back',
    label: translate('cuttingDock.cuttingParams.blankBack'),
  },
])
// 各种孔槽对应的类型
const HoldSlotTypeMap: Map<Omit<holeSlotType, ''>, 'slot' | 'hole'> = new Map([
  ['normalSlot', 'slot'],
  ['normalHole', 'hole'],
  ['normal_hole', 'hole'],
])
// end
// table数据
let dataSource = ref<DataSource[]>([])
// 动态bind
const bindValList = ref<TypeSide[]>([])

let count = ref(0)
// 每行数据的key
let key = ref(0)


onMounted(() => {
  // dataSource初始化
  bindValList.value = props.special_hole_slot_side_rules
  for (const it of props.special_hole_slot_side_rules) {
    if (!it.key) continue
    it.key = it.key.replace(/\d+$/, '') as holeSlotType
    dataSource.value.push({
      idx: ++count.value,
      key: String(++key.value),
      special: it.key,
      type: it.type,
      side: it.value,
      rules: it.rules,
      webId: it.webId ?? 'webId',
    })
  }
  // addStraightenerPX()
})

watch(
  () => props.special_hole_slot_side_rules,
  (val) => {
    // 更新的情况
    if (dataSource.value.length > 0) {
      count.value = 0
      dataSource.value.length = 0
      bindValList.value.length = 0
      for (const it of val) {
        if (!it.key) continue
        it.key = it.key.replace(/\d+$/, '') as holeSlotType
        // 动态邦迪值赋值
        bindValList.value.push({
          key: it.key,
          value: it.value,
          type: it.type,
          rules: it.rules,
        })
        dataSource.value.push({
          idx: ++count.value,
          key: String(++key.value),
          special: it.key,
          type: it.type,
          side: it.value,
          rules: it.rules,
          webId: it.webId ?? 'webId',
        })
      }
    }
  },
  {
    immediate: true,
  }
)

/**
 * @desc 修改下拉框
 * @param {DataSource} record 当前行行数据
 * @param {Number} idx 当前行索引 - 1
 * @author zhouchi 2024-08-22
 * */
function handleChange(record: DataSource, idx: number) {
  const curKey = bindValList.value[idx].key
  const type = (curKey && HoldSlotTypeMap.get(curKey)) || 'special'
  bindValList.value[idx].type = type
  if (type === 'hole') {
    const o = record.rules ?? {
      compare: '',
      value: '',
    }
    bindValList.value[idx].rules = o
  } else if (type === 'slot') {
    const o = record.rules ?? {
      long_side_compare: '',
      long_side_value: '',
      short_side_compare: '',
      short_side_value: '',
    }
    bindValList.value[idx].rules = o
  } else {
    bindValList.value[idx].rules = void 0
  }
  record.special = curKey
  record.type = type
  record.side = bindValList.value[idx].value
  record.rules = bindValList.value[idx].rules
}

/**
 * @description 确认删除单个规则
 */
function handleConfirmDelete(record: DataSource) {
  drgeTable.onDelete(record.key, record.idx)
  buryPoint({
    function_module: 'engraving',
    function_point: 'specialHoleSlotCustomDeleteRule',
  })
}

// 拖拽行/dataSource相关处理类
class DrgeTable {
  // 开始拖拽的行
  #dragstartRow: DataSource | undefined = void 0

  /**
   * @desc 判断拖拽前后位置是否一样,不一样则进行交换
   * @param {DataSource} dropVal 开始拖拽的行
   * @param {DataSource} curVal 当前行
   * @return {Boolean}
   * @author zhouchi 2024-08-21
   * */
  judgePositioAndUpdate = (dropVal: DataSource, curVal: DataSource) => {
    if (curVal.key !== dropVal.key) {
      this.#updateBindValList(curVal.idx - 1, dropVal)
      this.#updateDataSource(curVal.idx - 1, dropVal)
    }
  }
  /**
   * @desc 修改该源对象
   * @param {Number} curValIdx 当前行Idx
   * @param {DataSource} dropVal 拖拽行
   * @author zhouchi 2024-08-21
   * */
  #updateDataSource = (curValIdx: number, dropVal: DataSource) => {
    if (!dataSource.value) return

    dataSource.value.splice(dropVal.idx - 1, 1)
    // 将拖拽的行放置到当前行
    dataSource.value.splice(curValIdx, 0, dropVal)
    // 鼠标松开时 [当前索引后面的元素索引 --- 拖拽元素原之前的 元素]索引依次+1 || [当前索引后面的元素索引 --- 拖拽元素原之前的 元素]索引依次-1
    dataSource.value.map((ele, idx) => {
      ele.idx = idx + 1
    })
  }
  /**
   * @desc 修改绑定值
   * @param {Number} curValIdx 当前行Idx
   * @param {DataSource} dropVal 拖拽行
   * @author zhouchi 2024-08-21
   * */
  #updateBindValList = (curValIdx: number, dropVal: DataSource) => {
    let temp = bindValList.value[dropVal.idx - 1]
    bindValList.value.splice(dropVal.idx - 1, 1)
    bindValList.value.splice(curValIdx, 0, temp)
  }
  /**
   * @desc 添加一行数据
   * @author zhouchi 2024-08-21
   * */
  handleAdd = (
    special: holeSlotType = '',
    side: 'front' | 'back' = 'front'
  ) => {
    // 这里托底下 只要是拉直器、铰链杯就一定在大板反面
    if (special === 'straightener_slots_and_hinged_cup_holes') {
      side = 'back'
    }
    const newData: DataSource = {
      key: String(++key.value),
      idx: count.value + 1,
      type: '',
      special,
      side,
      rules: void 0,
      webId: genUniId(),
    }
    dataSource.value = [...dataSource.value, newData]
    bindValList.value.push({
      key: special,
      value: side,
      type: '',
      rules: void 0,
    })
    count.value = count.value + 1
  }
  /**
   * @desc 获取所有的特殊孔槽规则 此方法在 engraving.vue 被调用 一定注意!!!
   * @param
   * @author zhouchi 2024-08-21
   * */
  getAllRules = (): ResRules => {
    const special_hole_slot_side: TypeSide[] = []

    for (const item of dataSource.value) {
      if (!item.special || !item.side) {
        continue
      }

      const o: TypeSide = {
        key: item.special,
        value: item.side,
        rules: item.rules,
        type: item.type,
        webId: item.webId,
      }
      // 常规孔槽因为重名 所以要用id区分、
      if (item.type !== 'special') {
        o.key = o.key + item.idx
      }
      // o.rules = item.rules
      // // o[item.special] = item.side
      // o.key = item.special
      // o.value = item.side
      // o.webId = item.webId
      // o.type = item.type
      special_hole_slot_side.push(o)
    }
    return {
      is_open: props?.is_open ?? false,
      special_hole_slot_side,
    }
  }
  scrollToRowAndJudgeIsSave() {
    for (const item of dataSource.value) {
      // (孔槽类型)不能存在空字符的情况
      if (!item.special.trim()) {
        drgeTable.scrollIntoView('#type' + item.idx)
        return false
      }
      // 常规孔直径不能为空 常规槽槽长/短边至少要一个
      if (item.type !== 'special') {
        for (const key in item.rules) {
          const rules = item.rules as { [key: string]: string | Compare }
          if (!rules[key]?.trim()) {
            drgeTable.scrollIntoView(`#${key}` + item.idx)
            return false
          }
        }
      }
    }

    return true
  }
  /**
   * @desc 平滑的移动至锚点
   * @param idName
   */
  scrollIntoView(idName: string) {
    //当前选值为''的元素
    let curEle: any = document.querySelector(idName)
    // 追加样式
    if (curEle !== null) {
      curEle.style.border = '1px solid red'
      // 平滑的移动至锚点
      curEle.scrollIntoView({ behavior: 'smooth' })
      // throw new Error("holeOrSlotType is not a ''!")
      // 清除样式
      setTimeout(() => {
        curEle.style.border = ''
        curEle = null
      }, 1000)
    }
  }
  /**
   * @desc 删除当前行数据
   * @param {String} key 当前行key值
   * @author zhouchi 2024-08-21
   * */
  onDelete = (key: string, idx: number) => {
    const dataSourceLiss = [...dataSource.value]
    dataSource.value = dataSourceLiss.filter((item) => {
      // 若大于当前删除行 则索引依次-1
      if (item.idx > idx) {
        item.idx--
      }
      return item.key !== key
    })
    bindValList.value.splice(idx - 1, 1)
    count.value = dataSource.value.length
  }
  customRow = (record: DataSource) => {
    return {
      style: {
        cursor: 'pointer',
      },
      on: {
        // 鼠标移入
        mouseenter: (event: any) => {
          if (!event.target) return
          // 设置拖拽属性
          event.target.draggable = true
        },
        // 开始拖拽
        dragstart: (event: Event) => {
          // 阻止冒泡
          event.stopPropagation()
          // 得到源目标数据
          this.#dragstartRow = record
        },
        // 拖动元素经过的元素
        dragover: (event: Event) => {
          // 阻止默认行为
          event.preventDefault()
        },
        // 鼠标松开
        drop: (event: Event) => {
          // 阻止冒泡
          event.stopPropagation()
          // 判断拖拽前后位置是否一样
          this.judgePositioAndUpdate(this.#dragstartRow as DataSource, record)
        },
      },
    }
  }
}
const drgeTable = new DrgeTable()
const { handleAdd, getAllRules, scrollToRowAndJudgeIsSave } = drgeTable
expose({
  handleAdd,
  getAllRules,
  scrollToRowAndJudgeIsSave,
  bindValList,
})

return { __sfc: true,props, commonsDefult, columns, compareOpt, handleBlur, POSITIVE_DECIMAL_REG, correctPositiveDecimalRaw, normalizePositiveDecimalInput, formatPositiveDecimalInput, handleInput, holeSlotsOpt, surfanceOpt, HoldSlotTypeMap, dataSource, bindValList, count, key, emit, handleChange, handleConfirmDelete, DrgeTable, drgeTable, handleAdd, getAllRules, scrollToRowAndJudgeIsSave, translate }
}

})